import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isApiError, refusalMessage } from '../api';
import { earningsApi } from '../api/earnings';
import { clearDriverSession } from '../lib/driverSession';
import './driverEarningsWalletProfile.css';
import './driverWalletPremium.css';

function moneyOf(minor, currency) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format((Number(minor) || 0) / 100);
}

/** "+$7.54" or "−$0.83": an entry as the statement signs it, from the rider's side. */
function signedOf(minor, currency) {
  return `${Number(minor) < 0 ? '−' : '+'}${moneyOf(Math.abs(Number(minor) || 0), currency)}`;
}

/** "2 Oct": the operator's day, the date part of the moment the server wrote in its zone. */
function dayOf(iso) {
  const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''));

  return day
    ? new Date(Date.UTC(Number(day[1]), Number(day[2]) - 1, Number(day[3]))).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
    : '';
}

function IcInfo() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="7.2" stroke="currentColor" strokeWidth="1.4" fill="none" />
      <path d="M12 10.2V16M12 7.2v.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function IcTxEmpty() {
  return (
    <svg viewBox="0 0 24 24" width="40" height="40" fill="none" aria-hidden style={{ margin: '0 auto 0.65rem', display: 'block', color: '#9ca3af' }}>
      <rect x="4" y="6" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 11h8M8 15h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/** What a line says under its name: the delivery, how it was paid, where money went, and holds. */
function detailsOf(line) {
  return [
    line.delivery ? `#${line.delivery.reference}` : null,
    line.paid_by ? `paid by ${line.paid_by}` : null,
    line.method_label,
    line.destination ? `to ${line.destination}` : null,
    line.reference ? `ref ${line.reference}` : null,
    line.held_until ? `held until ${dayOf(line.held_until)}` : null,
    line.held_for_review ? 'held while the office checks this trip' : null,
    line.reversed ? 'reversed' : null,
  ].filter(Boolean);
}

/**
 * The rider's wallet (InGo ADR 0052): their balance with InGo, read from
 * their ledger, and its statement. Rio, 11 Oct 2026: a net balance the office
 * pays; earnings from clients who paid online are held a few days, then paid;
 * InGo's commission on a cash delivery comes off the balance by itself; a
 * rider pays what they owe at the office; and one credit limit, past which no
 * offers come.
 *
 * Only a statement (Rio: *"make sure this won't affect us when submitting our
 * app to google play store"*): no top-up, no transfer, no withdrawal. The
 * office sends a payout and records it; the rider reads it here.
 */
export default function DriverWalletPage({ earnings = earningsApi }) {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [lines, setLines] = useState([]);
  const [next, setNext] = useState(null);
  const [read, setRead] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [more, setMore] = useState(false);

  const refused = useCallback(
    (err) => {
      if (isApiError(err) && err.status === 401) {
        clearDriverSession();
        navigate('/driver/login', { replace: true });
        return;
      }
      setLoadErr(refusalMessage(err));
    },
    [navigate],
  );

  useEffect(() => {
    let cancelled = false;
    Promise.all([earnings.balance(), earnings.balanceLines()]).then(
      ([answer, page]) => {
        if (cancelled) return;
        setBalance(answer);
        setLines(page.lines);
        setNext(page.next);
        setRead(true);
      },
      (err) => !cancelled && refused(err),
    );

    return () => {
      cancelled = true;
    };
  }, [earnings, refused]);

  const showMore = async () => {
    setMore(true);
    try {
      const page = await earnings.balanceLines(next);
      setLines((shown) => [...shown, ...page.lines]);
      setNext(page.next);
    } catch (err) {
      refused(err);
    } finally {
      setMore(false);
    }
  };

  const currency = balance?.currency || 'USD';
  const owes = balance ? balance.balance_minor < 0 : false;
  const days = balance?.hold_days ?? 0;

  return (
    <div className="dvRoot dvRoot--wallet-premium" role="main">
      <header className="dvH">
        <span className="dv__heroSpacer" aria-hidden />
        <h1>My Wallet</h1>
        <span className="dv__heroSpacer" aria-hidden />
      </header>
      <div className="dvSc">
        {loadErr ? (
          <p className="dw-alert dw-alert--error" role="alert">
            {loadErr}
          </p>
        ) : null}

        <section className="dw-walletHero" aria-label="Your balance">
          <p className="dwbL1">{owes ? 'You owe InGo' : 'InGo owes you'}</p>
          <p className="dwbMain">{balance ? moneyOf(owes ? balance.owed_minor : balance.balance_minor, currency) : '…'}</p>
          {balance ? (
            <p className="dwbCodNote" role="note">
              Ready to be paid: {moneyOf(balance.available_minor, currency)}.
              {balance.held_minor > 0 ? ` Held for now: ${moneyOf(balance.held_minor, currency)}.` : ''}
              {balance.credit_limit_minor !== null ? ` Credit limit: ${moneyOf(balance.credit_limit_minor, currency)}.` : ''}
            </p>
          ) : null}
        </section>

        {balance?.over_credit ? (
          <p className="dw-alert dw-alert--error" role="alert">
            You owe {moneyOf(balance.owed_minor, currency)}, at or over your credit limit. You get no offers until you pay at the InGo office.
          </p>
        ) : null}

        <div className="dw-infoCard" role="note">
          <span className="dw-infoCardIcon" aria-hidden>
            <IcInfo />
          </span>
          <p className="dw-infoCardText">
            {days > 0
              ? `What you earn from clients who paid online is held ${days} ${days === 1 ? 'day' : 'days'}, then the InGo office pays you. `
              : 'What you earn from clients who paid online is paid to you by the InGo office. '}
            InGo's commission on cash deliveries comes off your balance by itself. Pay what you owe at the InGo office; they record it here.
          </p>
        </div>

        <h2 className="dvRsec">Statement</h2>
        {read && lines.length === 0 ? (
          <div className="dw-txEmpty" role="status">
            <IcTxEmpty />
            <p>Nothing on your statement yet.</p>
          </div>
        ) : null}
        {lines.length > 0 ? (
          <div className="dw-txList" aria-label="Statement">
            {lines.map((line) => (
              <div className="dvTxR" key={line.id} data-testid="statement-line">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="dvTxx">
                    {line.label} · {dayOf(line.at)}
                  </div>
                  {detailsOf(line).length > 0 ? <span className="dvTdt">{detailsOf(line).join(' · ')}</span> : null}
                  {line.note ? <span className="dvTdt"> “{line.note}”</span> : null}
                </div>
                <div
                  className={line.amount_minor < 0 ? 'dvTamt dvTamtC' : 'dvTamt'}
                  style={line.test_money || line.reversed ? { textDecoration: 'line-through', opacity: 0.55 } : undefined}
                >
                  {signedOf(line.amount_minor, line.currency)}
                </div>
              </div>
            ))}
          </div>
        ) : null}
        {next ? (
          <button type="button" className="dvPil" style={{ margin: '0.75rem auto', display: 'block' }} disabled={more} onClick={showMore}>
            {more ? 'Loading…' : 'Show older'}
          </button>
        ) : null}
        {balance?.counted_from ? <p className="dw-withdrawNote">Counted from {dayOf(balance.counted_from)}.</p> : null}
      </div>
    </div>
  );
}
